import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import AppShell from '../components/layout/AppShell';
import { getAppNavItems } from '../utils/appNavItems';
import { subscribeThreadMessages, sendMessage, markThreadRead } from '../utils/messaging';
import { useLocation } from 'react-router-dom';

export default function MessagesPage() {
  const { currentUser, isAdmin } = useAuth();
  const location = useLocation();
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (!currentUser?.uid) return undefined;
    markThreadRead(currentUser.uid, 'student').catch(() => {});
    return subscribeThreadMessages(currentUser.uid, setMessages);
  }, [currentUser?.uid]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  async function handleSend(e) {
    e.preventDefault();
    if (!draft.trim() || !currentUser?.uid) return;
    setSending(true);
    try {
      await sendMessage({
        fromUid: currentUser.uid,
        fromRole: 'student',
        fromName: currentUser.displayName || currentUser.email,
        studentUserId: currentUser.uid,
        text: draft,
      });
      setDraft('');
    } catch (err) {
      alert(err?.message || 'Failed to send message.');
    } finally {
      setSending(false);
    }
  }

  function formatTime(ts, clientAt) {
    if (!ts && !clientAt) return '';
    const d = ts?.toDate ? ts.toDate() : ts ? new Date(ts) : new Date(clientAt);
    return d.toLocaleString('en-ZA', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  }

  const navItems = getAppNavItems(isAdmin, location.pathname);

  return (
    <AppShell navItems={navItems}>
      <div style={s.header}>
        <h1 style={s.title}>Messages</h1>
        <p style={s.sub}>Chat with the Success Bridge Academy administration team.</p>
      </div>

      <div style={s.chat}>
        <div style={s.messages}>
          {messages.length === 0 ? (
            <p style={s.empty}>No messages yet. Send a question to the admin team.</p>
          ) : (
            messages.map((m) => (
              <div key={m.id} style={s.bubble(m.fromRole === 'student')}>
                <span style={s.meta}>{m.fromName || m.fromRole} · {formatTime(m.createdAt, m.clientAt)}</span>
                <p style={s.text}>{m.text}</p>
              </div>
            ))
          )}
          <div ref={bottomRef} />
        </div>
        <form onSubmit={handleSend} style={s.compose}>
          <input
            style={s.input}
            placeholder="Type your message…"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
          <button type="submit" disabled={sending || !draft.trim()} style={s.sendBtn(sending)}>
            Send
          </button>
        </form>
      </div>
    </AppShell>
  );
}

const s = {
  header: { marginBottom: 20 },
  title: { fontSize: 22, fontWeight: 800, color: '#1b365d', marginBottom: 6 },
  sub: { fontSize: 14, color: '#64748b', margin: 0 },
  chat: {
    background: '#fff',
    borderRadius: 16,
    border: '1px solid #e2e8f0',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 420,
  },
  messages: { flex: 1, overflowY: 'auto', padding: 20, display: 'flex', flexDirection: 'column', gap: 10 },
  empty: { color: '#94a3b8', textAlign: 'center', padding: 40 },
  bubble: (isStudent) => ({
    alignSelf: isStudent ? 'flex-end' : 'flex-start',
    maxWidth: '75%',
    background: isStudent ? '#4b00d1' : '#f1f5f9',
    color: isStudent ? '#fff' : '#1e293b',
    borderRadius: 12,
    padding: '10px 14px',
  }),
  meta: { fontSize: 10, opacity: 0.75, display: 'block', marginBottom: 4 },
  text: { margin: 0, fontSize: 14, lineHeight: 1.5 },
  compose: { display: 'flex', gap: 10, padding: 16, borderTop: '1px solid #e2e8f0' },
  input: { flex: 1, padding: '12px 14px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 14 },
  sendBtn: (disabled) => ({
    padding: '12px 20px',
    borderRadius: 10,
    border: 'none',
    background: disabled ? '#94a3b8' : '#059669',
    color: '#fff',
    fontWeight: 700,
    cursor: disabled ? 'not-allowed' : 'pointer',
  }),
};
